import { nextRestStep } from './pitch-count-rules';

// Pocket Pager: what the Lock Screen / Dynamic Island Live Activity says during
// a live game, so a coach can read the game off a closed phone. Pure: the live
// engine passes in what it already computes and this turns it into short lines.
//
// Copy is information, never instruction (bench-coach voice), and no em dashes:
// these strings are user-facing.

export type PagerLevel = 'ok' | 'warn' | 'limit';

export interface PagerState {
  inning: number;
  pitcherName?: string;
  pitches?: number;
  pitchLine?: string;
  limitNote?: string;
  nextNote?: string;
  level?: PagerLevel;
}

export interface PagerInput {
  styleOfPlay: string;
  inning: number;
  pitcherName?: string | null;
  /** This game's live count plus pitches thrown in earlier games today. */
  pitchesToday: number;
  /** Innings pitched today, this game included (USSSA's innings rules). */
  inningsToday?: number;
  /** The resolved age-group rules (or custom rules) the engine is using. */
  ageRules?: any;
  /** Set only when the plan puts a different pitcher on the mound next inning. */
  nextPitcherName?: string | null;
  /** Players whose position changes next inning per the plan; null when there's no plan. */
  nextInningChanges?: number | null;
}

/** "Andres Rodriguez" → "Andres R.", same as the defense grid's name column. */
export const pagerName = (name: string) => {
  const [first, ...rest] = (name || '').trim().split(/\s+/);
  const last = rest[rest.length - 1];
  return last ? `${first} ${last[0]}.` : first || '';
};

// Same cut points as the live diamond's pitch count color (DiamondView), so the
// Lock Screen and the screen in the coach's hand never disagree.
const levelFor = (used: number, max: number | undefined): PagerLevel => {
  if (!max || max <= 0 || max >= 999) return 'ok';
  const ratio = used / max;
  return ratio > 0.8 ? 'limit' : ratio > 0.5 ? 'warn' : 'ok';
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function buildPagerState(input: PagerInput): PagerState {
  const state: PagerState = { inning: input.inning };

  if (input.styleOfPlay === 'playerspitch' && input.pitcherName) {
    const rules = input.ageRules || {};
    const p = Math.max(0, Math.round(input.pitchesToday || 0));
    state.pitcherName = pagerName(input.pitcherName);
    state.pitches = p;

    if (rules.logicType === 'innings') {
      // USSSA: innings, not pitches, are the limit. Pitches stay visible because
      // coaches still watch them.
      const inn = input.inningsToday ?? 0;
      const max: number | undefined = rules.dailyMaxInnings;
      const block: number | undefined = rules.nextDayBlockInnings;
      state.pitchLine = max ? `${p} pitches, ${inn} of ${max} inn` : `${p} pitches`;
      if (max && inn >= max) state.limitNote = "At today's inning limit";
      else if (block && inn >= block) state.limitNote = "Can't pitch tomorrow";
      else if (block) state.limitNote = `${plural(block - inn, 'more inning', 'more innings')}: can't pitch tomorrow`;
      state.level = levelFor(inn, max);
    } else {
      const max: number | undefined = rules.logicType === 'outs'
        ? (rules.dailyMaxPitches ?? rules.dailyMax)
        : rules.dailyMax;
      const hasMax = !!max && max > 0 && max < 999;
      state.pitchLine = hasMax ? `${p} of ${max} pitches` : `${p} pitches`;
      const step = rules.logicType === 'outs' ? null : nextRestStep(p, rules.restThresholds || []);
      if (hasMax && p >= max!) state.limitNote = "At today's limit";
      else if (step) state.limitNote = `${step.pitchesUntil} more: ${plural(step.daysRest, 'day', 'days')} rest`;
      else if (hasMax) state.limitNote = `${max! - p} left today`;
      state.level = levelFor(p, hasMax ? max : undefined);
    }
  }

  if (input.styleOfPlay === 'playerspitch' && input.nextPitcherName) {
    state.nextNote = `Next inning: ${pagerName(input.nextPitcherName)} pitches`;
  } else if (input.nextInningChanges != null) {
    state.nextNote = input.nextInningChanges === 0
      ? 'Next inning: same defense'
      : `Next inning: ${plural(input.nextInningChanges, 'position change', 'position changes')}`;
  }

  return state;
}
